/**
 * @voltxampere/web — ERC (Electrical Rule Checker) store (Sprint 56c)
 *
 * Wires the engine-side `runERC` into the web app. Watches
 * `currentCircuit` + `currentDCResult` and re-runs the check after
 * a debounce window (default 300 ms).
 *
 * Two re-run triggers:
 *   1. Circuit mutation → topology-only rules re-evaluated.
 *   2. DC result settled → re-run with `dcResult` so DC-aware rules
 *      (saturation, overvoltage) get accurate readings.
 *
 * Per-violation `ignoredKeys` set persists in `localStorage` so the
 * user's "ignore" choices survive a reload without polluting the
 * Circuit document.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { effect, signal } from "@preact/signals-core";
import { runERC, type ERCReport, type ERCViolation } from "@voltxampere/engine";

import { currentCircuit } from "./circuit.js";
import { currentDCResult, type DCResultLike } from "./dc-result.js";

export const DEFAULT_ERC_DEBOUNCE_MS = 300;

export const ercReport = signal<ERCReport | null>(null);
export const ercLastRunMs = signal<number | null>(null);
export const ercRunDurationMs = signal<number | null>(null);

/** Whether the violations panel is currently open. Topbar badge
 *  click toggles it; the panel's close button + Esc reset it. */
export const ercPanelOpen = signal<boolean>(false);

/** Show vs hide ignored violations inside the panel. Per-session
 *  only — does NOT persist (users can re-toggle freely). */
export const ercPanelShowIgnored = signal<boolean>(false);

export function toggleErcPanel(): void {
  ercPanelOpen.value = !ercPanelOpen.value;
}

export function closeErcPanel(): void {
  ercPanelOpen.value = false;
}

const IGNORED_KEYS_STORAGE_KEY = "vxa.erc.ignored-violations";

function loadIgnoredKeys(): Set<string> {
  try {
    const raw = window.localStorage.getItem(IGNORED_KEYS_STORAGE_KEY);
    if (raw === null) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((s): s is string => typeof s === "string"));
  } catch {
    return new Set();
  }
}

function persistIgnoredKeys(keys: Set<string>): void {
  try {
    window.localStorage.setItem(IGNORED_KEYS_STORAGE_KEY, JSON.stringify([...keys]));
  } catch {
    // localStorage failure — proceed in-memory, user will re-ignore.
  }
}

export const ignoredViolationKeys = signal<ReadonlySet<string>>(loadIgnoredKeys());

/**
 * Stable per-violation key — used for the ignore set + UI list keys.
 * Combines ruleId + sorted meta entries so two violations differing
 * only by component target hash to distinct keys.
 */
export function violationKey(v: ERCViolation): string {
  const metaEntries = Object.entries(v.meta).sort(([a], [b]) => a.localeCompare(b));
  const metaStr = metaEntries.map(([k, val]) => `${k}=${String(val)}`).join("|");
  return `${v.ruleId}::${metaStr}`;
}

export function ignoreViolation(v: ERCViolation): void {
  const next = new Set(ignoredViolationKeys.value);
  next.add(violationKey(v));
  ignoredViolationKeys.value = next;
  persistIgnoredKeys(next);
}

export function unignoreViolation(v: ERCViolation): void {
  const next = new Set(ignoredViolationKeys.value);
  next.delete(violationKey(v));
  ignoredViolationKeys.value = next;
  persistIgnoredKeys(next);
}

export function clearIgnoredViolations(): void {
  ignoredViolationKeys.value = new Set();
  persistIgnoredKeys(new Set());
}

/** Filtered violations after applying the user's ignore set. */
export function visibleViolations(report: ERCReport | null): readonly ERCViolation[] {
  if (report === null) return [];
  const ignored = ignoredViolationKeys.value;
  return report.violations.filter((v) => !ignored.has(violationKey(v)));
}

/** Count visible (non-ignored) violations by severity. */
export function visibleViolationCounts(report: ERCReport | null): {
  readonly error: number;
  readonly warn: number;
  readonly info: number;
} {
  const visible = visibleViolations(report);
  let error = 0;
  let warn = 0;
  let info = 0;
  for (const v of visible) {
    if (v.severity === "error") error += 1;
    else if (v.severity === "warn") warn += 1;
    else info += 1;
  }
  return { error, warn, info };
}

interface BootErcOpts {
  /** Override debounce window. Tests use fakeTimers + 0. */
  readonly debounceMs?: number;
}

let pendingTimer: ReturnType<typeof setTimeout> | null = null;
let activeUnsubscribe: (() => void) | null = null;

/**
 * Activate ERC auto-run. Subscribes to `currentCircuit` AND
 * `currentDCResult`; either changing schedules a debounced re-run.
 * Returns teardown.
 */
export function bootErc(opts: BootErcOpts = {}): () => void {
  if (activeUnsubscribe) return activeUnsubscribe;
  const debounceMs = opts.debounceMs ?? DEFAULT_ERC_DEBOUNCE_MS;

  let firstRun = true;
  const dispose = effect(() => {
    // Read both signals so the effect re-subscribes when either
    // changes. (preact-signals tracks reads.)
    const circuit = currentCircuit.value;
    const dc = currentDCResult.value;
    if (firstRun) {
      firstRun = false;
      // Run synchronously on first effect tick so initial fixture
      // load surfaces violations without waiting for an edit.
      if (circuit !== null) {
        runOnce(circuit, dc);
      }
      return;
    }
    if (circuit === null) {
      ercReport.value = null;
      return;
    }
    schedule(circuit, dc, debounceMs);
  });

  activeUnsubscribe = (): void => {
    dispose();
    if (pendingTimer !== null) {
      clearTimeout(pendingTimer);
      pendingTimer = null;
    }
    activeUnsubscribe = null;
    firstRun = true;
  };
  return activeUnsubscribe;
}

function schedule(
  circuit: ReturnType<typeof currentCircuit.peek>,
  dc: DCResultLike | null,
  debounceMs: number,
): void {
  if (pendingTimer !== null) clearTimeout(pendingTimer);
  pendingTimer = setTimeout(() => {
    pendingTimer = null;
    if (circuit) runOnce(circuit, dc);
  }, debounceMs);
}

function runOnce(
  circuit: NonNullable<ReturnType<typeof currentCircuit.peek>>,
  dc: DCResultLike | null,
): void {
  const t0 = performance.now();
  // S56c — DC-aware bridge is deferred. The web app stores DCResultLike
  // (Record-keyed) while the engine ERC rules expect a full DCResult
  // with `nodeVoltages` / `branchCurrents` as ReadonlyMap. Passing the
  // Record-based shape would crash the saturation rule on its first
  // `.get()` call. Topology rules cover the auto-run hook for now;
  // saturation + overvoltage live in the parity-harness smoke test
  // (Sprint 56c-4) and a `bridgeDcResultForErc` adapter is tracked in
  // ADR-0091 §S56c backlog.
  void dc;
  const report = runERC(circuit);
  const t1 = performance.now();
  ercReport.value = report;
  ercLastRunMs.value = Date.now();
  ercRunDurationMs.value = t1 - t0;
}

/** Test helper — full cold reset. */
export function resetErcForTests(): void {
  activeUnsubscribe?.();
  activeUnsubscribe = null;
  if (pendingTimer !== null) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
  ercReport.value = null;
  ercLastRunMs.value = null;
  ercRunDurationMs.value = null;
  ignoredViolationKeys.value = new Set();
}
